import { Autosuggest, AutosuggestProps } from '@cloudscape-design/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePeopleSearch } from 'src/store/people/usePeopleSearch';
import { PeopleSuggestion } from 'src/types/orbit/People';
import { badgePhotoUrl } from 'src/utils/badgePhoto';

/**
 * Avatar size (px) for suggestion rows. Sized to fit Cloudscape's `iconSvg`
 * slot without overlapping the option's text — the slot reserves ~24px of
 * horizontal space per its own layout rules, and going larger clips into the
 * label column. 24px is also the size other Amazon pickers use (FCM,
 * PayrollPayments, PaySvNext) for consistency.
 */
const BADGE_PHOTO_SIZE_PX = 24;

/**
 * Circular avatar. Renders the phonetool badge photo for {@code login}; if
 * the CDN returns an error (unknown alias, network hiccup) the img is hidden
 * so the row falls back to just the label — no broken-image glyph.
 */
const BadgeAvatar = ({ login }: { login: string }) => {
  return (
    <span
      aria-hidden="true"
      style={{
        // inline-flex + verticalAlign so the avatar centers with the label
        // whether Cloudscape's option row uses flex or baseline layout.
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        verticalAlign: 'middle',
        width: BADGE_PHOTO_SIZE_PX,
        height: BADGE_PHOTO_SIZE_PX,
        // Cloudscape's icon-slot gap only applies when the child is an <svg>;
        // for our <img>-based avatar we own the horizontal breathing room.
        marginInlineEnd: 10,
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: 'var(--color-background-input-disabled, #eaeded)',
      }}
    >
      <img
        src={badgePhotoUrl(login)}
        alt=""
        width={BADGE_PHOTO_SIZE_PX}
        height={BADGE_PHOTO_SIZE_PX}
        loading="lazy"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onError={(e) => {
          // Hide the broken image so the row renders cleanly even for aliases
          // without a phonetool photo (contractors, service accounts, etc).
          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
    </span>
  );
};

export interface EmployeePickerProps {
  /** Program scope for the suggest call. */
  'programId': string;
  /** Currently committed login (canonical, lowercase), or empty. */
  'value': string;
  /**
   * Called when the user selects a suggestion. Always yields a login; fullName
   * is best-effort display data.
   */
  'onSelect': (login: string, fullName?: string) => void;
  /** Called when the field is cleared. */
  'onClear'?: () => void;
  'placeholder'?: string;
  'disabled'?: boolean;
  'invalid'?: boolean;
  'data-testid'?: string;
}

/**
 * Single label used both for the dropdown row and — once selected — for the
 * text that lands in the input. "Name (login)" reads well as a single line
 * and gives the user visual confirmation of the alias they picked.
 */
const formatLabel = (suggestion: PeopleSuggestion): string =>
  suggestion.fullName ? `${suggestion.fullName} (${suggestion.login})` : suggestion.login;

/**
 * Reusable employee autocomplete. The committed value is always a canonical
 * login (never raw text): selecting an option calls {@link onSelect} with that
 * option's login. Backed by Orbit SuggestPeople via {@link usePeopleSearch}.
 */
const EmployeePicker = ({
  programId,
  value,
  onSelect,
  onClear,
  placeholder = 'Search by alias or name',
  disabled = false,
  invalid = false,
  'data-testid': dataTestId,
}: EmployeePickerProps) => {
  // Input text the user is typing; distinct from the committed login `value`.
  const [text, setText] = useState(value);

  // Tracks the login we just committed via `handleSelect` so that the
  // "sync-value-to-text" effect below can distinguish "parent echoed back the
  // login we just picked" (skip: keep the formatted `Name (login)` label)
  // from "parent externally reset value" (act: clobber text to match). See
  // handleSelect for where this is set.
  const lastCommittedLogin = useRef<string | null>(null);

  // Keep the displayed text in sync when the parent resets `value` externally
  // (e.g. filter clear-all, modal close-and-reopen, RHF form reset). Without
  // this, the picker keeps showing the previously-selected label even though
  // the committed login is now empty. Guarded by `lastCommittedLogin` so a
  // just-completed selection's formatted label is not overwritten when the
  // parent immediately echoes back the committed login as `value`.
  useEffect(() => {
    if (value !== lastCommittedLogin.current) {
      setText(value);
    }
    // Clear so a subsequent external write of the same value (e.g. parent
    // resets to '', then later sets the same login again) is not silently
    // suppressed.
    lastCommittedLogin.current = null;
  }, [value]);

  const { suggestions, isLoading, isError } = usePeopleSearch(programId, text);

  const options: AutosuggestProps.Options = useMemo(
    () =>
      suggestions.map((s) => ({
        value: s.login,
        label: formatLabel(s),
        // iconSvg accepts arbitrary ReactNode. We use it to render the
        // properly-sized badge photo instead of Cloudscape's tiny iconUrl slot.
        iconSvg: <BadgeAvatar login={s.login} />,
      })),
    [suggestions],
  );

  const statusType: AutosuggestProps.StatusType = isError
    ? 'error'
    : isLoading
      ? 'loading'
      : 'finished';

  const handleChange = (newValue: string) => {
    setText(newValue);
    if (newValue === '') {
      onClear?.();
    }
  };

  const handleSelect = (selectedValue: string) => {
    // selectedValue is the option value === login. Only commit if it matches a
    // returned suggestion; raw free text never becomes a committed login.
    const match = suggestions.find((s) => s.login === selectedValue);
    if (match) {
      // Record the login we are about to commit so the value-sync effect
      // does not clobber the formatted `Name (login)` label when the parent
      // re-renders with `value === match.login`.
      lastCommittedLogin.current = match.login;
      setText(formatLabel(match));
      onSelect(match.login, match.fullName);
    }
  };

  return (
    <Autosuggest
      value={text}
      options={options}
      onChange={({ detail }) => handleChange(detail.value)}
      onSelect={({ detail }) => handleSelect(detail.value)}
      filteringType="manual"
      statusType={statusType}
      loadingText="Searching employees..."
      errorText="Could not load suggestions"
      empty="No matching employees"
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid}
      ariaLabel="Employee search"
      // Contract: only committed suggestions become logins. Do NOT surface a
      // free-text fallback — that would let users "select" a non-employee.
      enteredTextLabel={(v) => v}
      data-testid={dataTestId}
    />
  );
};

export default EmployeePicker;
